import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ArrowRight, CheckCircle2, ListChecks, PlayCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { EnterpriseSalesNav } from '../components/enterprise/EnterpriseSalesNav';
import { useSEO } from '../lib/seo';

type DemoTenantContext = {
  orgId: string;
  name: string;
  slug: string;
  createdAt: string;
};

const DEMO_TENANT_CONTEXT_STORAGE_KEY = 'tension_enterprise_demo_tenant_v1';

export default function EnterpriseStartHere() {
  useSEO({
    title: 'Start Here | Enterprise Demo Setup',
    description:
      'Step-by-step wizard to create a demo tenant, route user check-ins into enterprise analytics, and validate live enterprise controls.',
    canonical: '/enterprise/start-here',
    ogType: 'website',
  });

  const [demoContext, setDemoContext] = useState<DemoTenantContext | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DEMO_TENANT_CONTEXT_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as DemoTenantContext;
      if (!parsed?.orgId) return;
      setDemoContext(parsed);
    } catch {
      // Ignore malformed local storage content.
    }
  }, []);

  return (
    <div className="enterprise-public-shell min-h-screen bg-[radial-gradient(circle_at_top,rgba(30,64,175,0.2),transparent_35%),linear-gradient(180deg,#020617_0%,#0f172a_50%,#020617_100%)]">
      <EnterpriseSalesNav variant="dark" />
      <div className="mx-auto max-w-6xl space-y-6 px-4 py-8">
        <header className="enterprise-hero-shell">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-300">Enterprise onboarding wizard</p>
          <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-100 md:text-4xl">Start here: get a fully working enterprise demo</h1>
          <p className="mt-3 max-w-4xl text-sm leading-relaxed text-slate-300 md:text-base">
            This flow gives you a real, hands-on setup: create a demo tenant, route consumer check-ins to that org, and verify live enterprise analytics.
          </p>
          {demoContext ? (
            <p className="mt-3 text-sm text-emerald-200">
              Latest demo tenant loaded: {demoContext.name} ({demoContext.orgId})
            </p>
          ) : null}
        </header>

        <section className="grid gap-4 md:grid-cols-2">
          <Card className="enterprise-surface-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Step 1</p>
            <h2 className="mt-1 text-xl font-semibold text-slate-100">Create demo tenant</h2>
            <p className="mt-2 text-sm text-slate-300">
              Open Enterprise Command Center and use <span className="font-semibold text-slate-100">Configure sales demo tenant (one click)</span>.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white" asChild>
                <Link to="/enterprise/admin">
                  Open Command Center
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </Card>

          <Card className="enterprise-surface-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Step 2</p>
            <h2 className="mt-1 text-xl font-semibold text-slate-100">Set org context for check-ins</h2>
            <p className="mt-2 text-sm text-slate-300">
              In Command Center, click <span className="font-semibold text-slate-100">Use this org for consumer check-ins</span> so submitted user check-ins attach to your enterprise org.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100" asChild>
                <Link to={demoContext ? `/enterprise/admin?org=${encodeURIComponent(demoContext.orgId)}` : '/enterprise/admin'}>
                  Open org-scoped Command Center
                </Link>
              </Button>
            </div>
          </Card>

          <Card className="enterprise-surface-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Step 3</p>
            <h2 className="mt-1 text-xl font-semibold text-slate-100">Submit check-ins from user flow</h2>
            <p className="mt-2 text-sm text-slate-300">
              Use the consumer check-in page while signed in. Submit 3-5 check-ins to populate trend and risk signals.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100" asChild>
                <Link to="/work/home">Open employee workspace home</Link>
              </Button>
              <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100" asChild>
                <Link to="/work/check-in">
                  Open employee check-in
                  <PlayCircle className="ml-1 h-4 w-4" />
                </Link>
              </Button>
              <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100" asChild>
                <Link to="/daily-check-in">Open legacy consumer check-in</Link>
              </Button>
            </div>
          </Card>

          <Card className="enterprise-surface-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">Step 4</p>
            <h2 className="mt-1 text-xl font-semibold text-slate-100">Verify enterprise analytics + controls</h2>
            <p className="mt-2 text-sm text-slate-300">
              Return to Command Center and click Load overview, Load seats, and Wave previews. Export CSV/PDF for buyer conversations.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100" asChild>
                <Link to={demoContext ? `/enterprise/admin?org=${encodeURIComponent(demoContext.orgId)}` : '/enterprise/admin'}>
                  Validate live metrics
                  <ListChecks className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </Card>
        </section>

        <Card className="enterprise-surface-card p-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-100">
            <CheckCircle2 className="h-5 w-5 text-emerald-300" aria-hidden />
            Success criteria
          </h2>
          <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-slate-300">
            <li>Org is provisioned and visible in Command Center.</li>
            <li>Enterprise workspace check-ins are submitted while signed in.</li>
            <li>Overview metrics and risk signals update for the same org.</li>
            <li>Wave 2/Wave 3 previews load with non-empty content.</li>
          </ul>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white" asChild>
              <Link to="/enterprise/how-it-works">Open data flow explainer</Link>
            </Button>
            <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100" asChild>
              <Link to="/enterprise/live-demo">Open hands-on sandbox demo</Link>
            </Button>
            <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100" asChild>
              <Link to="/work/timeline">Open workspace timeline</Link>
            </Button>
            <Button variant="outline" className="border-slate-500 bg-slate-900 text-slate-100" asChild>
              <Link to="/work/insights">Open workspace insights</Link>
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

